<template>
  <template v-if="access.read || true">
    <SearchInput
      v-model="controller.searchText.value"
      v-if="!isSubTable && settings?.searchpanel"
      :searchAway="settings?.searchAway !== false"
    />

    <div
      :class="{
        'table-frame': frame || (verified && !valid),
        'border-danger': verified && !valid,
        'transparent-row': settings?.transparentTr,
      }"
    >
      <div class="navtable">
        <PanelFun
          :items="panelFun"
          v-if="settings?.panelFun"
          classPanelFun="btn-group btn-group-sm mr-1"
        />
        <div v-else></div>

        <div class="form-inline" v-if="!isSubTable && pages">
          <Pagination
            @setpage="selectPage"
            v-model="controller.page.value"
            :pages="paginationPages"
            v-if="paginationPages > 1"
          />

          <select
            class="form-control form-control-sm"
            :value="controller.limit.value"
            @change="onLimitChange"
          >
            <option
              v-for="element of settings?.limits"
              :key="element"
              :value="element"
            >
              {{ element }}
            </option>
          </select>
        </div>
      </div>

      <div class="table-responsive pb-1" :style="containerStyle">
        <table
          class="table table-striped table-bordered table-hover table-sm unselectable"
          @keydown="onKeyDown"
          tabindex="-1"
        >
          <GridHead
            :controller="controller"
            :checkedRowSelect="checkedRowSelect"
            @sort="onSort"
          />
          <GridBody
            :controller="controller"
            :crossot="settings?.crossot"
            @active="activeRow"
            @select="selectRow"
          />
          <GridFooter :controller="controller" v-if="settings?.footer" />
        </table>
      </div>
    </div>

    <div class="table-invalid-feedback" v-if="feedback && verified">
      {{ feedback }}
    </div>
  </template>
</template>

<script>
//Вывод метки и звездочки если есть валидатор
import { onMounted, computed, defineComponent, onUnmounted, ref } from "vue";

import SearchInput from "@/components/SearchInput.vue";
import Pagination from "@/components/Pagination";
import PanelFun from "@/components/PanelFun";
import GridHead from "./GridHead";
import GridBody from "./GridBody";
import GridFooter from "./GridFooter";
import DBGridController from "./controller";

export default defineComponent({
  inheritAttrs: false,

  emits: ["active", "select", "store", "getEttn"],

  components: {
    SearchInput,
    Pagination,
    PanelFun,
    GridHead,
    GridBody,
    GridFooter,
  },

  props: {
    store: {
      type: Object,
      default: null,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      default: null,
    },
    field: {
      type: String,
      default: null,
    },
    validation: {
      type: Boolean,
      default: true,
    },
    pages: {
      type: Boolean,
      default: true,
    },
    frame: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const controller = new DBGridController(props, emit);

    if (!controller.store) {
      console.error("Grid storage not installed");

      return {
        access: { read: false },
      };
    }

    emit("store", controller.store);

    const validation = () =>
      controller.validation ? controller.validation() : () => false;

    const isSubTable = computed(() => {
      if (controller.settings?.allowSubtablePagination) {
        return false;
      }
      return !!(
        controller.store.model.subtable || controller.store.model.offLine
      );
    });

    const paginationPages = computed(() => {
      // Локальный расчет количества страниц для вложенных таблиц с allowSubtablePagination
      if (
        controller.settings?.allowSubtablePagination &&
        controller.store?.model?.subtable
      ) {
        const rows = controller.store?.data?.rows || [];
        const limit = controller.limit?.value || rows.length || 1;
        if (!rows.length) return 1;
        return Math.max(1, Math.ceil(rows.length / limit));
      }

      return controller.pages.value || 1;
    });

    onMounted(async () => {
      // Load column settings from backend or localStorage
      if (controller.loadColumnSettings) {
        const settings = await controller.loadColumnSettings();
        if (settings && controller.applyColumnSettings) {
          controller.applyColumnSettings(settings);
        }
      } else if (
        controller.settings?.localStorageKey &&
        localStorage[controller.settings.localStorageKey]
      ) {
        // Legacy support for old localStorage approach
        const fields = JSON.parse(
          localStorage[controller.settings.localStorageKey],
        );

        if (fields.length > 0 && controller.setFields)
          controller.setFields(fields);
      }

      if (!isSubTable.value) {
        controller.store.fetchOptions["filters"] =
          controller.settings?.filters || [];
        controller.store.fetchOptions["data"] = controller.settings?.data || {};

        if (controller.settings?.id)
          controller.store.fetchOptions.id = controller.settings.id;
        if (controller.settings?.owner)
          controller.store.fetchOptions.owner = controller.settings.owner;

        //Установка лимита запускает получение данных
        controller.store.fetchOptions.limit =
          controller.limit?.value || controller.settings?.limit || 25;

        // await controller.store.fetchData();
      }

      if (props.form && props.field) {
        if (
          controller.stateField?.validation &&
          Object.keys(controller.stateField.validation).length
        )
          props.form.addValidation(validation);
      }

      if (controller.settings?.onCreate)
        controller.settings.onCreate(controller);

      if (controller.settings?.autoOpen && controller.fetchData)
        controller.fetchData();
    });

    onUnmounted(() => {
      if (props.form && props.field) {
        if (
          controller.stateField?.validation &&
          Object.keys(controller.stateField.validation).length
        )
          props.form.delValidation(validation);
      }
    });

    const containerStyle = {};

    if (controller.settings.height) {
      containerStyle["height"] = controller.settings.height;
      containerStyle["overflow"] = "auto";
    }

    const onLimitChange = (event) => {
      const value = Number(event?.target?.value);
      if (Number.isFinite(value) && value > 0) {
        controller.limit.value = value;
      }
    };

    return {
      controller,
      openRow: (data) => (controller.openRow ? controller.openRow(data) : null),
      addRow: (data) => (controller.addRow ? controller.addRow(data) : null),
      settings: controller.settings || {},
      containerStyle,
      onKeyDown: (event) =>
        controller.onKeyDown ? controller.onKeyDown(event) : null,
      onSort: (sort) => (controller.onSort ? controller.onSort(sort) : null),
      selectRow: (data) =>
        controller.selectRow ? controller.selectRow(data) : null,
      activeRow: (position) =>
        controller.activeRow ? controller.activeRow(position) : null,
      selectPage: (page) =>
        controller.selectPage ? controller.selectPage(page) : null,
      onLimitChange,
      panelFun: controller.panelFun || ref([]),
      access: computed(
        () =>
          controller.access || {
            read: false,
            create: false,
            update: false,
            delete: false,
          },
      ),
      feedback: controller.feedback || ref(""),
      verified: controller.verified || ref(false),
      valid: controller.valid || ref(true),
      checkedRowSelect: controller.checkedRowSelect || ref(false),
      isSubTable,
      paginationPages,
    };
  },
});
</script>

<style>
.table thead th,
.table thead td {
  outline: 0;
  position: relative;
  -webkit-touch-callout: none;
  /* iOS Safari */
  -webkit-user-select: none;
  /* Chrome/Safari/Opera */
  -khtml-user-select: none;
  /* Konqueror */
  -moz-user-select: none;
  /* Firefox */
  -ms-user-select: none;
  /* Internet Explorer/Edge */
  user-select: none;
}

.table thead .sorting_asc,
.table thead .sorting_desc,
.table thead .sorting {
  cursor: pointer;
}

.table thead .sorting,
.table thead .sorting_asc,
.table thead .sorting_desc,
.table thead .sorting_asc_disabled,
.table thead .sorting_desc_disabled {
  padding-right: 1.5rem;
}

.table thead .sorting:before,
.table thead .sorting:after,
.table thead .sorting_asc:after,
.table thead .sorting_desc:after,
.table thead .sorting_asc_disabled:after,
.table thead .sorting_desc_disabled:after {
  content: "";
  font-family: "emed-icons";
  position: absolute;
  top: 50%;
  right: 0.5rem;
  font-size: 0.75rem;
  margin-top: -0.375rem;
  display: inline-block;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.table thead .sorting:before {
  content: "\e907";
  margin-top: -0.125rem;
  opacity: 0.5;
}

.table thead .sorting:after {
  content: "\e90b";
  margin-top: -0.625rem;
  opacity: 0.5;
}

.table thead .sorting_asc:after {
  content: "\e90b";
  opacity: 1;
}

.table thead .sorting_desc:after {
  content: "\e907";
  opacity: 1;
}

.table thead .sorting_asc_disabled:after {
  content: "";
  opacity: 0.25;
}

.table thead .sorting_desc_disabled:after {
  content: "";
  opacity: 0.25;
}

.table-invalid-feedback {
  width: 100%;
  font-size: 90%;
  color: #f44336;
}

.table-frame {
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.1875rem;
  margin-bottom: 0.625rem;
}

.table-responsive > .table-bordered {
  border: 0 !important;
}

.table-responsive > .table {
  box-sizing: border-box;
  margin-bottom: 0;
  border-collapse: collapse;
}

.table-responsive {
  padding-right: 1px;
}
</style>
